import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import {
  DeleteLibraryFilesModal_RemoveFilesFromGroupLibrary,
  DeleteLibraryFilesModal_RemoveFilesFromGroupLibraryVariables,
} from './graphql-types/DeleteLibraryFilesModal_RemoveFilesFromGroupLibrary';
import {
  DeleteLibraryFolderModal_DeleteGroupLibraryFolder,
  DeleteLibraryFolderModal_DeleteGroupLibraryFolderVariables,
} from './graphql-types/DeleteLibraryFolderModal_DeleteGroupLibraryFolder';

const DELETE_FOLDER = gql`
  mutation DeleteLibraryFolderModal_DeleteGroupLibraryFolder(
    $input: DeleteGroupLibraryFolderInput!
  ) {
    deleteGroupLibraryFolder(input: $input)
  }
`;

const DELETE_FILES = gql`
  mutation DeleteLibraryFilesModal_RemoveFilesFromGroupLibrary(
    $input: RemoveFilesFromGroupLibraryInput!
  ) {
    removeFilesFromGroupLibrary(input: $input)
  }
`;

export const DeleteLibraryResourceModal: React.FC<{
  onClose: () => void;
  isOpen: boolean;
  versionFileIds?: string[];
  libraryFolderId?: string;
  groupId?: string;
  onAfterDeleteFiles?: () => void;
}> = ({
  onClose,
  isOpen,
  versionFileIds,
  groupId,
  libraryFolderId,
  onAfterDeleteFiles,
}) => {
  const toast = useToast();
  const [deleteFile, { loading: isFileDeleting }] = useMutation<
    DeleteLibraryFilesModal_RemoveFilesFromGroupLibrary,
    DeleteLibraryFilesModal_RemoveFilesFromGroupLibraryVariables
  >(DELETE_FILES, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  const [deleteFolder, { loading: isFolderDeleting }] = useMutation<
    DeleteLibraryFolderModal_DeleteGroupLibraryFolder,
    DeleteLibraryFolderModal_DeleteGroupLibraryFolderVariables
  >(DELETE_FOLDER, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  const onDeleteFile = async () => {
    if (!groupId || !versionFileIds?.length) {
      return;
    }

    try {
      await deleteFile({
        variables: {
          input: {
            groupId,
            versionFileIds,
          },
        },
      });
      onAfterDeleteFiles?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Error while deleting the file(s), please try again',
        status: 'error',
      });
    }
  };

  const onDeleteFolder = async () => {
    if (!libraryFolderId) {
      return;
    }

    try {
      await deleteFolder({
        variables: {
          input: {
            libraryFolderId,
          },
        },
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error while deleting the folder, please try again',
        status: 'error',
      });
    }
  };

  const isFolder = !!libraryFolderId;
  const hasMultipleFiles = versionFileIds && versionFileIds?.length > 1;

  if (!((groupId && versionFileIds?.length) || libraryFolderId)) {
    return null;
  }

  const isLoading = isFileDeleting || isFolderDeleting;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Delete {isFolder ? 'Folder' : hasMultipleFiles ? 'Files' : 'File'}?
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>
            This will permanently remove the{' '}
            {isFolder ? 'folder' : hasMultipleFiles ? 'files' : 'file'}.
          </Text>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button
              onClick={onClose}
              variant="outline"
              colorScheme="gray"
              isDisabled={isLoading}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              isLoading={isLoading}
              onClick={isFolder ? onDeleteFolder : onDeleteFile}
            >
              Delete
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
